import { useMemo, useState } from "react";
import {
    Grid,
    FormControl,
    FormLabel,
    Stack,
    Button,
    MenuItem,
    useTheme,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { FormInput, FormDateTimePicker } from "src/components";
import { TimeTypes } from "src/types";
import { useFormikContext } from "formik";
import { TripRequestFormValues } from "../types";
import { useAppSelector } from "src/hooks";
import { ReturnTripTypes, validateByCalendar } from "../helpers";
import {
    authSelector,
    navigatorSelector,
} from "src/modules/auth/redux/authSlice";
import { pick } from "lodash";
import { Client } from "src/modules/auth/types";
import { tripsAPI } from "src/modules/trips/redux/tripSlice";
import ReturnTripOptions from "./ReturnTripOptions";
import moment from "moment";

interface Props {
    client: Client;
}

const TimeSelector = ({ client }: Props) => {
    const { config, programs } = useAppSelector(authSelector);
    const isNavigator = useAppSelector(navigatorSelector);
    const [editTime, setEditTime] = useState(false);
    const { values, errors, validateForm, setValues, setFieldValue } =
        useFormikContext<TripRequestFormValues>();

    const [validateServiceCalendar, { isLoading }] =
        tripsAPI.useValidateServiceCalendarMutation();

    const theme = useTheme();
    const isMobileDevice = useMediaQuery(theme.breakpoints.down("md"));
    const preferences = config?.ConnectPreferences;
    const disablePast = moment(values.apptTime).isBefore(new Date());

    const service = useMemo(() => {
        return programs.find((item) => item.programName === values.tripProgram);
    }, [values.tripProgram, programs]);

    const canRequestFutureTrip = useMemo(() => {
        // allow the rider to adjust the time if no service is available. services are fetched by time. disabling the time picker would prevent this.
        if (!service) return true;
        return isNavigator
            ? service?.riderCanScheduleFutureTrip
                ? true
                : false
            : config.ConnectPreferences.NegotiationRange === 999 // This value is specifically configured for this purpose in Paraplan
              ? false
              : true;
    }, [config.ConnectPreferences.NegotiationRange, isNavigator, service]);

    async function handleCalendarValidation(date: Date) {
        try {
            if (!isNavigator) {
                const { message } = await validateByCalendar(
                    client,
                    date,
                    values.tripProgram
                );
                return message;
            }

            if (!service) return "";
            // use rider selected date if availabe and servie allows for future trip selection
            const requestDate = service.riderCanScheduleFutureTrip
                ? values.requestTime
                : date;
            const isValid = await validateServiceCalendar({
                id: service.databaseID,
                date: moment(requestDate).format("YYYY-MM-DDTHH:mm:ss"),
            }).unwrap();

            return isValid
                ? ""
                : `${values.tripProgram} is not available at this time`;
            // todo: get exact time service is valid for when onDemandAccessToken can call serviceCalendar
        } catch (error) {}

        // never let this fail
        return "";
    }

    function handleChange(date: Date) {
        if (isNavigator) {
            // Ensure date is maintained when switching between future and current services with a return trip
            // @see ReturnTripOptions and TripProgram for other  implementation
            if (values.returnType !== ReturnTripTypes.oneWay) {
                setFieldValue(
                    "returnTime",
                    moment(values.returnTime)
                        .set({
                            ...pick(moment(date).toObject(), [
                                "date",
                                "months",
                                "years",
                            ]),
                        })
                        .toDate(),
                    false
                );
                validateForm(); // Ensures return time is validated after apptTime change
            }
            // stash user selected time for restoration when switching between services, see TripPrograms component
            setFieldValue("requestTime", date, false);
            return;
        }

        // only update fields for Paraplan, update return time, add two hours
        setValues(
            {
                ...values,
                pickUpTime: date,
                dropOffTime: date,
                returnTime: moment(date).add(2, "h").toDate(),
            },
            false
        );
    }

    return (
        <Grid
            item
            xs={12}
            sx={{
                p: 2,
                mb: 4,
                boxShadow: 1,
                borderRadius: 1,
            }}
        >
            <FormControl
                component="fieldset"
                sx={{
                    mb: 4,
                    display: "flex",
                    gap: 2,
                }}
            >
                <FormLabel
                    component="legend"
                    sx={{
                        mb: 2,
                        fontSize: 20,
                        color: "primary.main",
                    }}
                >
                    Times
                </FormLabel>

                {/* pick up times */}
                <Stack
                    spacing={2}
                    direction={{ xs: "column", md: "row" }}
                    alignItems="center"
                >
                    <FormInput
                        id="timeType"
                        name="timeType"
                        label="I want to be"
                        select
                        sx={{ flexGrow: 1 }}
                        SelectProps={{ native: isMobileDevice }}
                        disabled={!preferences.CanCreateApptRequest}
                    >
                        {Object.values(TimeTypes).map((item, index) =>
                            isMobileDevice ? (
                                <option key={index} value={item}>
                                    {item}
                                </option>
                            ) : (
                                <MenuItem key={index} value={item}>
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </FormInput>

                    {editTime || errors.apptTime || canRequestFutureTrip ? (
                        <FormDateTimePicker
                            disablePast
                            reduceAnimations
                            name="apptTime"
                            label={
                                values.timeType === TimeTypes.droppedOff
                                    ? "Appointment Date and Time"
                                    : "Trip Date and Pick Up Time"
                            }
                            sx={{ flexGrow: 1 }}
                            validate={(date) => {
                                return handleCalendarValidation(date);
                            }}
                            onChange={handleChange}
                        />
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            sx={{ flexGrow: 0.25, py: 1.25 }}
                            onClick={() => setEditTime(true)}
                            disabled={isLoading || !canRequestFutureTrip}
                        >
                            Now
                        </Button>
                    )}

                    {isLoading && (
                        <CircularProgress color="primary" size={24} />
                    )}
                </Stack>

                {/* return trip */}
                {preferences?.CanRequestReturnTrip && (
                    <ReturnTripOptions
                        client={client}
                        disablePast={disablePast}
                    />
                )}
            </FormControl>
        </Grid>
    );
};

export default TimeSelector;
